import React, { useContext, useState } from "react";
import { Button, Form, Modal, Segmented } from "antd";
import {
  CalendarOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import { LANDING_PAGE_URL } from "../../Constants";

// Invitation page themes (Social Gathering events). Keys and main colours mirror the landing
// page's lib/invitationThemes.ts - keep the two in step.
export const INVITATION_THEMES = [
  { key: "ivory", bg: "#f7f1e5", panel: "#fbf6ea", accent: "#b38a32", ink: "#14204a", line: "#d9c391" },
  { key: "navy", bg: "#0b1f5e", panel: "#13296e", accent: "#d9b868", ink: "#f6efe0", line: "rgba(200,163,90,0.6)" },
  { key: "blush", bg: "#fbf0ef", panel: "#fdf5f4", accent: "#b5657a", ink: "#3b2233", line: "#e8c3c9" },
  { key: "lavender", bg: "#f3f0fa", panel: "#f8f6fd", accent: "#8a6fc4", ink: "#221780", line: "#d6cdf0" },
];

export const DEFAULT_INVITATION_THEME = "ivory";

const SERIF = "'Amiri', 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
const SAMPLE_TITLE = { ar: "سارة وعمر", en: "Sara & Omar" };

// A small version of the invitation page in a theme's colours: a soft glow standing in for the
// background photo, the arch with the event's title and divider, and the date / time / place cards.
const ThemeSketch = ({ theme, title }) => (
  <div
    style={{
      background: `radial-gradient(ellipse at 50% 22%, ${theme.accent}33, transparent 62%), ${theme.bg}`,
      height: 176,
      borderRadius: 10,
      padding: "14px 20px 10px",
    }}
    className="flex flex-col items-center"
    aria-hidden="true"
  >
    <div
      style={{
        width: "78%",
        height: 86,
        background: theme.panel,
        border: `1px solid ${theme.line}`,
        borderRadius: "70px 70px 8px 8px",
        boxShadow: `0 2px 8px ${theme.accent}22`,
      }}
      className="flex flex-col items-center justify-end pb-2 px-2"
    >
      <span
        dir="auto"
        style={{ fontFamily: SERIF, color: theme.accent, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}
        className="max-w-full truncate"
      >
        {title}
      </span>
      <span className="flex items-center gap-1 my-1" style={{ width: "62%" }}>
        <span style={{ flex: 1, height: 1, background: theme.line }} />
        <span style={{ width: 5, height: 5, background: theme.accent, transform: "rotate(45deg)" }} />
        <span style={{ flex: 1, height: 1, background: theme.line }} />
      </span>
      <span style={{ width: "46%", height: 3, borderRadius: 2, background: theme.ink, opacity: 0.4 }} />
    </div>
    {[CalendarOutlined, ClockCircleOutlined, EnvironmentOutlined].map((Icon, row) => (
      <div
        key={row}
        style={{
          width: "84%",
          height: 17,
          marginTop: 5,
          background: theme.panel,
          border: `1px solid ${theme.line}`,
          borderRadius: 6,
        }}
        className="flex items-center gap-2 px-2"
      >
        <Icon style={{ color: theme.accent, fontSize: 9 }} />
        <span style={{ width: `${58 - row * 12}%`, height: 3, borderRadius: 2, background: theme.ink, opacity: 0.5 }} />
      </div>
    ))}
  </div>
);

// Three overlapping dots: the theme's background, accent and text colours.
const Palette = ({ theme }) => (
  <span className="inline-flex items-center" aria-hidden="true">
    {[theme.bg, theme.accent, theme.ink].map((color, i) => (
      <span
        key={i}
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: color,
          border: "1px solid rgba(0,0,0,0.12)",
          marginInlineStart: i ? -4 : 0,
        }}
      />
    ))}
  </span>
);

// The grid follows the width of its card, not the screen: the picker sits in a narrow column on
// some pages and a wide one on others. Two across by default, four when there is room.
const PICKER_CSS = `
  .itp-wrap { container-type: inline-size; }
  .itp-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
  @container (min-width: 700px) { .itp-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
  .itp-card { transition: transform .15s ease, box-shadow .15s ease; }
  .itp-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(15, 23, 42, 0.10); }
  .itp-eye { opacity: 0; transition: opacity .15s ease; }
  .itp-card:hover .itp-eye, .itp-eye:focus-visible { opacity: 1; }
  @media (hover: none) { .itp-eye { opacity: 1; } }
`;

/**
 * Theme picker for the invitation landing page, used as a Form.Item control (value / onChange).
 * The preview shows the real landing page in a phone frame; it needs the saved event's slug.
 */
const InvitationThemePicker = ({ value, onChange, landingSlug }) => {
  const { t, i18n } = useTranslation();
  const { primaryColor } = useContext(AppContext);
  const form = Form.useFormInstance();
  const titleEn = Form.useWatch("title", form);
  const titleAr = Form.useWatch("arabicTitle", form);
  const isArabic = !!i18n.language?.startsWith("ar");
  // The event's own title once it has one, so each card shows how it would look for this event.
  const sampleTitle =
    ((isArabic ? titleAr || titleEn : titleEn || titleAr) || "").trim() || SAMPLE_TITLE[isArabic ? "ar" : "en"];
  const selected = value || DEFAULT_INVITATION_THEME;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(selected);
  const [saveFirstOpen, setSaveFirstOpen] = useState(false);

  const themeName = (key) => t(`eventForm.invitationTheme.themes.${key}.name`);
  const previewUrl = landingSlug
    ? `${LANDING_PAGE_URL}/events/${encodeURIComponent(landingSlug)}?previewTheme=${previewTheme}`
    : "";

  const openPreview = (themeKey = selected) => {
    // An unsaved event has no landing page yet: explain that instead of opening an empty frame.
    if (!landingSlug) {
      setSaveFirstOpen(true);
      return;
    }
    setPreviewTheme(themeKey);
    setPreviewOpen(true);
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">{t("eventForm.invitationTheme.hint")}</p>

      <style>{PICKER_CSS}</style>
      <div className="itp-wrap">
        <div className="itp-grid">
          {INVITATION_THEMES.map((theme) => {
            const isSelected = theme.key === selected;
            return (
              <div
                key={theme.key}
                className="itp-card relative rounded-2xl bg-white"
                style={{
                  border: `2px solid ${isSelected ? theme.accent : "#e5e7eb"}`,
                  boxShadow: isSelected ? `0 0 0 4px ${theme.accent}33` : undefined,
                }}
              >
                <button
                  type="button"
                  onClick={() => onChange?.(theme.key)}
                  aria-pressed={isSelected}
                  className="block w-full text-start rounded-2xl p-2 bg-transparent"
                  style={{ border: "none", cursor: "pointer" }}
                >
                  <ThemeSketch theme={theme} title={sampleTitle} />
                  <div className="px-1.5 pt-3 pb-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900">{themeName(theme.key)}</span>
                      <Palette theme={theme} />
                    </div>
                    <div className="text-xs text-gray-500 leading-snug mt-0.5">
                      {t(`eventForm.invitationTheme.themes.${theme.key}.bestFor`)}
                    </div>
                  </div>
                </button>
                {isSelected && (
                  <span
                    className="absolute top-4 start-4 inline-flex items-center gap-1 rounded-full text-xs font-semibold text-white"
                    style={{ background: primaryColor, padding: "2px 10px 2px 8px" }}
                  >
                    <CheckOutlined style={{ fontSize: 11 }} />
                    {t("eventForm.invitationTheme.selected")}
                  </span>
                )}
                <Button
                  className="itp-eye absolute top-3.5 end-3.5"
                  shape="circle"
                  size="small"
                  icon={<EyeOutlined />}
                  aria-label={t("eventForm.invitationTheme.preview")}
                  title={t("eventForm.invitationTheme.preview")}
                  onClick={() => openPreview(theme.key)}
                />
              </div>
            );
          })}
        </div>
      </div>

      <Button
        type="primary"
        size="large"
        icon={<EyeOutlined />}
        block
        className="mt-4"
        onClick={() => openPreview()}
        style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
      >
        {t("eventForm.invitationTheme.preview")}
      </Button>
      {!landingSlug && (
        <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mt-2.5">
          <InfoCircleOutlined style={{ color: primaryColor }} />
          {t("eventForm.invitationTheme.saveToPreview")}
        </p>
      )}

      <Modal
        open={saveFirstOpen}
        onCancel={() => setSaveFirstOpen(false)}
        width={360}
        centered
        footer={null}
        closable={false}
      >
        <div className="flex flex-col items-center text-center pt-2" role="alertdialog">
          <span
            className="inline-flex items-center justify-center rounded-full"
            style={{ width: 56, height: 56, background: `${primaryColor}1a`, color: primaryColor, fontSize: 26 }}
          >
            <SaveOutlined />
          </span>
          <h3 className="text-base font-semibold text-gray-900 mt-4 mb-1">
            {t("eventForm.invitationTheme.saveFirstTitle")}
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed mb-5">{t("eventForm.invitationTheme.saveFirstBody")}</p>
          <Button
            type="primary"
            block
            size="large"
            onClick={() => setSaveFirstOpen(false)}
            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            {t("eventForm.invitationTheme.gotIt")}
          </Button>
        </div>
      </Modal>

      <Modal
        title={t("eventForm.invitationTheme.previewTitle")}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width={460}
        style={{ top: 20 }}
        destroyOnClose
        footer={[
          <Button key="open" onClick={() => window.open(previewUrl, "_blank")} disabled={!previewUrl}>
            {t("eventForm.buttons.openInNewTab")}
          </Button>,
          // The theme being previewed is already the saved choice: say so, instead of a greyed-out button.
          previewTheme === selected ? (
            <Button
              key="use"
              icon={<CheckCircleFilled />}
              aria-disabled="true"
              tabIndex={-1}
              style={{ color: "#389e0d", borderColor: "#b7eb8f", background: "#f6ffed", cursor: "default" }}
            >
              {t("eventForm.invitationTheme.currentlySelected")}
            </Button>
          ) : (
            <Button
              key="use"
              type="primary"
              onClick={() => {
                onChange?.(previewTheme);
                setPreviewOpen(false);
              }}
              style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
            >
              {t("eventForm.invitationTheme.useTheme")}
            </Button>
          ),
        ]}
      >
        <Segmented
          block
          value={previewTheme}
          onChange={setPreviewTheme}
          options={INVITATION_THEMES.map((theme) => ({
            value: theme.key,
            // A check marks the theme the event currently uses, whichever tab is being previewed.
            label: (
              <span title={theme.key === selected ? t("eventForm.invitationTheme.currentlySelected") : undefined}>
                {theme.key === selected && <CheckCircleFilled style={{ color: "#389e0d", marginInlineEnd: 4, fontSize: 12 }} />}
                {themeName(theme.key)}
              </span>
            ),
          }))}
        />
        {/* A phone-sized frame, so the page renders its mobile layout as guests see it. */}
        <div
          className="mx-auto mt-4 overflow-hidden"
          style={{
            width: 390,
            maxWidth: "100%",
            height: "min(720px, calc(100vh - 260px))",
            border: "10px solid #111827",
            borderRadius: 36,
          }}
        >
          {previewUrl && (
            <iframe
              key={previewUrl}
              src={previewUrl}
              title={t("eventForm.invitationTheme.previewTitle")}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default InvitationThemePicker;
